<template>
  <Page
    id="StreamListView"
    :title="$t('common.pageTitle')"
    :primaryAction="{
      label: $t('common.createStream.action'),
      onAction: createStream
    }"
    :secondaryActions="[{
      label: $t('common.Notes'),
      onAction: () => eventHub.$emit('showAccountNotes', 'STREAM')
    }]"
    size="fullWidth"
  >
    <Card>
      <Filters
        :filters="[
          {
            key: 'status',
            label: $t('common.Status'),
            type: 'select',
            multiple: true,
            options: [
              {value: 'draft', label: $t('common.status.draft') },
              {value: 'published', label: $t('common.status.published') },
              {value: 'deleted', label: $t('common.status.archived') },
              {value: 'disabled', label: $t('common.status.disabled') }
            ]
          }
        ]"
        :shortcuts="[
          {
            label: $t('common.Status'),
            items: [
              {
                label: $t('common.Any'),
                onAction: () => setFilter('status', null)
              },
              {
                label: $t('common.status.draft'),
                onAction: () => setFilter('status', ['draft'])
              },
              {
                label: $t('common.status.published'),
                onAction: () => setFilter('status', ['published'])
              },
              {
                label: $t('common.status.archived'),
                onAction: () => setFilter('status', ['deleted'])
              },
              {
                label: $t('common.status.disabled'),
                onAction: () => setFilter('status', ['disabled'])
              }
            ]
          }
        ]"
        @update:applied-filters="updateFilter"
        :applied-filters="appliedFilters"
        :query.sync="query"
      />
      <DataTable
        :loading="!filteredStreams"
        :columns="columns"
        :rows="filteredStreams || []"
        :row-actions="rowActionGenerator"
      >
        <template v-slot:col:id="{row}">
          <PageLink @click="onClickRow(row)">{{row.id}}</PageLink>
        </template>
        <template v-slot:col:status="{row}">
          <Badge :status="getStreamBadge(row).status">{{getStreamBadge(row).label}}</Badge>
        </template>
      </DataTable>
    </Card>
  </Page>
</template>

<script>
import {getStreamBadge} from '../utils.ts'
import _ from 'lodash'

export default {
  inject: ['$t'],
  data() {
    return {
      query: null,
      appliedFilters: {
        status: null
      }
    }
  },
  computed: {
    streams() {
      const streams = this.$store.state.streams
      return streams && streams.sort(streamSort)
    },
    filteredStreams() {
      const statuses = this.appliedFilters.status
      const query = this.query && this.query.toLowerCase()
      return this.streams && this.streams.filter(d => {
        const statusMatch = !statuses || statuses.indexOf(d.status) !== -1
          || (statuses.indexOf('disabled') !== -1 ? d.isDisabled : false)
        const queryMatch = !query || d.name.toLowerCase().indexOf(query) !== -1 
        return statusMatch && queryMatch
      })
    },
    columns() {
      return [
        {
          key: 'id',
          label: this.$t('common.ID'),
          format: 'integer',
          fixed: true
        },
        {
          key: 'name',
          label: this.$t('common.Name')
        },
        {
          key: 'eventType',
          label: this.$t('node.TRIGGER.name'),
          format: val => this.$t(`trigger.type.${val}.label`)
        },
        {
          key: 'status',
          label: this.$t('common.Status')
        },
        {
          key: 'updatedOn',
          label: this.$t('common.Last Updated'),
          format: 'dateTimeShort'
        }
      ]
    },
    rowActionGenerator() {
      return (row) => {
        const isPublished = row.status === 'published'
        // const isArchived = row.status === 'deleted'
        const isDraft = row.status === 'draft'
        const canArchive = (
          isDraft ||
          (isPublished && row.isDisabled)
        )
        return [
          {
            label: row.isDisabled
              ? this.$t('common.Enable')
              : this.$t('common.Disable'),
            disabled: !isPublished,
            onAction: async (row) => {
              const newDisabled = !row.isDisabled
              await this.$store.dispatch('setStreamDisabledById', {
                id: row.id,
                isDisabled: newDisabled
              })
              await this.$store.dispatch('loadStreams')
              this.$ui.toast({
                title: newDisabled ? this.$t('common.Stream disabled') : this.$t('common.Stream enabled')
              })
            }
          },
          {
            label: this.$t('common.Duplicate'),
            onAction: () => this.copyStream(row)
          },
          {
            label: this.$t('common.Archive'),
            onAction: () => this.archiveStream(row),
            disabled: !canArchive
          }
        ]
      }
    }
  },
  async created() {
    await this.setFilterFromQuery()

    this.$watch('$route', async () => {
      await this.setFilterFromQuery()
    }, { deep: true })
    
    this.loadStreams()
  },
  methods: {
    getStreamBadge,
    async setFilterFromQuery() {
      this.appliedFilters = {}
      if (this.$route.query) {
        if (Array.isArray(this.$route.query.status)) {
          this.appliedFilters.status = this.$route.query.status
        } else if (this.$route.query.status) {
          this.appliedFilters.status = [this.$route.query.status]
        } else {
          this.appliedFilters.status = null
        }
      }
      if (_.isEmpty(this.$route.query)) {
        this.appliedFilters.status = [
          'published',
          'draft'
        ]
      }

      await this.$router.replace({ name: 'AccountStreams', query: this.appliedFilters})
      .catch(this.duplicateRouteCatch)

    },
    loadStreams() {
      return this.$store.dispatch('loadStreams')
    },
    createStream() {
      this.$ui.prompt({
        title: this.$t('common.createStream.title'),
        content: this.$t('common.createStream.content'),
        primaryActionLabel: this.$t('common.createStream.action'),
        inputValidator: (name) => {
          return name.length > 0
        },
        onSubmit: async (name) => {
          const stream = await this.$store.dispatch('createStream', name)
          this.$ui.toast({
            title: this.$t('common.createdStreamX', {name: `"${name}"` })
          })
          await this.loadStreams()
          this.editStreamById(stream.id)
        }
      })
    },
    copyStream(row) {
      this.$ui.prompt({
        title: this.$t('common.copyStream.title'),
        content: this.$t('common.copyStream.content'),
        primaryActionLabel: this.$t('common.copyStream.action'),
        inputValue: this.$t('common.copyStreamInputValue', { name: `${row.name}` }),
        inputValidator: (name) => {
          const lowerName = name.toLowerCase()
          const match = this.streams.find(d => {
            return d.status !== 'deleted' &&
              d.name.toLowerCase() === lowerName
          })
          if (match) {
            return this.$t('common.copyStream.errorDuplicateName')
          }
          return name.length > 0
        },
        onSubmit: async (name) => {
          const stream = await this.$store.dispatch('copyStream', {
            id: row.id,
            name: name
          })
          this.$ui.toast({
            title: this.$t('common.createdStreamX', { name: `"${stream.name}"` })
          })
          await this.loadStreams()
          this.editStreamById(stream.id)
        }
      })
    },
    archiveStream(row) {
      this.$ui.confirm({
        title: this.$t('common.archiveStream.title'),
        content: this.$t('common.archiveStream.content'),
        primaryActionLabel: this.$t('common.archiveStream.action'),
        primaryActionType: 'destructive',
        onConfirm: async () => {
          await this.$store.dispatch('archiveStreamById', row.id)
          this.loadStreams()
          this.$ui.toast({
            title: this.$t('common.archivedStreamX', { name: `"${row.name}"` })
          })
        }
      })
    },
    editStreamById(id) {
      // this.$store.dispatch('editStreamById', id)
      this.$router.push(`/account/streams/edit/${id}/`)
    },
    onClickRow(row) {
      this.editStreamById(row.id)
    },
    updateUrl() {
      this.$router.push({ name: 'AccountStreams', query: this.appliedFilters})
      .catch(this.duplicateRouteCatch)
    },
    setFilter(key, value) {
      this.appliedFilters[key] = value
      this.updateUrl()
    },
    updateFilter(filter) {
      this.appliedFilters = filter
      this.updateUrl()
    }
  }
}

function getStreamSortPriority(stream) {
  const {status, isDisabled} = stream
  if (status === 'published') {
    return isDisabled ? 1 : 0
  }
  if (status === 'draft') {
    return 2
  }
  return 3
}

function streamSort(a, b) {
  const sA = getStreamSortPriority(a)
  const sB = getStreamSortPriority(b)
  if (sA === sB) {
    const nameA = a.name.toLowerCase()
    const nameB = b.name.toLowerCase()
    if (nameA < nameB) return -1
    if (nameA > nameB) return 1
    return 0
  }
  return sA - sB
}
</script>